import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px, SWReactCountryIcons } from "@similarweb/icons";
import type { ConversionRatesOverviewListParams } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import {
    Badge,
    Card,
    ChangeValue,
    IconButton,
    ListItemPlaceholderLoader,
    ProgressBar,
    TitlePlaceholderLoader,
    Tooltip,
} from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { resolveEmptyStateType } from "app/pages/website-analysis/TrafficAndEngagement/conversionRateEmptyStateUtils";
import type { ICommonWidgetProps, IWidgetPOPParams } from "app/pages/website-analysis/TrafficAndEngagement/types";
import DurationService from "app/services/DurationService";
import { openHooksModalV5 } from "app/services/ModalService";
import { PREVIOUS_YEAR } from "app/services/periodOverPeriodTypes";

import { CONVERSION_RATE_TRANSLATION_KEYS, conversionRateFormatters } from "./const";
import { ConversionRateEmptyState } from "./ConversionRateEmptyState";
import type { IConversionRateAvailability } from "./useConversionRateAvailability";
import { useConversionRateData } from "./useConversionRateData";

const PERIOD_COLORS = {
    current: colorsPalettes.blue[400],
    compared: colorsPalettes.orange[400],
} as const;

interface IConversionRatePOPWidgetProps extends Pick<
    ICommonWidgetProps,
    "dateRangeText" | "countryText" | "countryCode"
> {
    params: IWidgetPOPParams;
    availability: IConversionRateAvailability;
}

interface IHeaderProps {
    title: string;
    tooltip: string;
    dateRangeText: string;
    countryText: string;
    countryCode: number;
}

interface IPeriodRowProps {
    periodText: string;
    value: number;
    barColor: string;
    changeValue?: number | null;
    changeLabel?: string;
}

const ConversionRateCardHeader = ({ title, tooltip, dateRangeText, countryText, countryCode }: IHeaderProps) => {
    const { durationTriggerProps, countryTriggerProps } = useSelectorTrigger("conversion-rate-pop-widget");

    return (
        <Card.Header withDivider>
            <Card.Title>
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="h5_2"
                        data-automation="conversion-rate-pop-widget-title"
                    >
                        {title}
                    </Text>
                    <span data-automation="conversion-rate-pop-widget-title-badge">
                        <Badge type="new" />
                    </span>
                    <span data-automation="conversion-rate-pop-widget-title-tooltip">
                        <Tooltip content={tooltip}>
                            <Info24px
                                size={24}
                                color={colorsPalettes.carbon[200]}
                                data-automation="conversion-rate-pop-widget-title-info-icon"
                            />
                        </Tooltip>
                    </span>
                </Flex>
                <Card.Query>
                    <Card.QueryItem
                        iconName="daily-ranking"
                        data-automation="conversion-rate-pop-widget-date-range"
                        {...durationTriggerProps}
                    >
                        {dateRangeText}
                    </Card.QueryItem>
                    <Flex
                        gap="8px"
                        alignItems="center"
                        data-automation="conversion-rate-pop-widget-country"
                        {...countryTriggerProps}
                    >
                        <SWReactCountryIcons
                            countryCode={countryCode}
                            size="xs"
                        />
                        <Text
                            fontVariant="chip1"
                            color={colorsPalettes.carbon[400]}
                        >
                            {countryText}
                        </Text>
                    </Flex>
                </Card.Query>
            </Card.Title>
        </Card.Header>
    );
};

const ConversionRatePeriodRow = ({ periodText, value, barColor, changeValue = null, changeLabel }: IPeriodRowProps) => {
    const hasChange = changeValue != null;

    return (
        <Flex
            flexDirection="column"
            gap="8px"
        >
            <Flex
                alignItems="center"
                gap="6px"
            >
                <Text fontVariant="input2">{periodText}</Text>
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[250]}
                >
                    •
                </Text>
                <Text
                    data-automation="conversion-rate-pop-value"
                    fontVariant="input2Bold"
                >
                    {conversionRateFormatters.conversionRate(value)}
                </Text>
                {hasChange ? (
                    <>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[250]}
                        >
                            •
                        </Text>
                        <Flex
                            alignItems="center"
                            gap="4px"
                        >
                            <ChangeValue
                                value={conversionRateFormatters.change(changeValue)}
                                isDecrease={changeValue < 0}
                                unsigned={changeValue === 0}
                            />
                            <Text
                                fontVariant="input2"
                                color={changeValue < 0 ? colorsPalettes.red["400_P"] : colorsPalettes.green["400_P"]}
                            >
                                {changeLabel}
                            </Text>
                        </Flex>
                    </>
                ) : null}
            </Flex>
            <ProgressBar
                data-automation="conversion-rate-pop-progress-bar"
                values={[value]}
                colorSet={[barColor]}
                height="16px"
            />
        </Flex>
    );
};

const ConversionRatePeriodRowLoader = () => (
    <Flex
        flexDirection="column"
        gap="8px"
    >
        <TitlePlaceholderLoader />
        <ListItemPlaceholderLoader />
    </Flex>
);

export const ConversionRatePOPWidget = ({
    params,
    dateRangeText,
    countryText,
    countryCode,
    availability,
}: IConversionRatePOPWidgetProps) => {
    const { translate } = useTranslate();
    const { state: availabilityState, isLoading: isAvailabilityLoading } = availability;
    const { duration, comparedDuration } = params;
    const periodOverPeriodCompareType = DurationService.getPeriodOverPeriodCompareType();
    const compareModeLabel =
        periodOverPeriodCompareType === PREVIOUS_YEAR ? translate("common.yoy") : translate("common.pop");

    const overviewApiParamsBase = {
        country: params.country,
        keys: [params.key],
        isWindow: params.isWindow,
        includeSubdomains: !params.isWWW,
    } satisfies Omit<ConversionRatesOverviewListParams, "from" | "to">;

    const currentApiParams = {
        ...overviewApiParamsBase,
        from: params.from,
        to: params.to,
    } satisfies ConversionRatesOverviewListParams;

    const comparedApiParams = {
        ...overviewApiParamsBase,
        from: params.compareFrom,
        to: params.compareTo,
    } satisfies ConversionRatesOverviewListParams;

    const {
        data: currentData,
        isLoading: isCurrentDataLoading,
        isError: isCurrentDataError,
        emptyStateType: currentEmptyStateType,
    } = useConversionRateData(currentApiParams, { enabled: availabilityState === "available" });

    const {
        data: comparedData,
        isLoading: isComparedDataLoading,
        isError: isComparedDataError,
        emptyStateType: comparedEmptyStateType,
    } = useConversionRateData(comparedApiParams, { enabled: availabilityState === "available" });

    const currentPeriodValue = currentData?.[0]?.conversionRate;
    const comparedPeriodValue = comparedData?.[0]?.conversionRate;

    const hasData = currentPeriodValue != null && comparedPeriodValue != null;
    const isError = isCurrentDataError || isComparedDataError;
    const isLoading = isAvailabilityLoading || isCurrentDataLoading || isComparedDataLoading;
    const resolvedEmptyStateType = resolveEmptyStateType(currentEmptyStateType, comparedEmptyStateType);

    const durationObject = DurationService.getDurationData(duration, comparedDuration);
    const changeValue = hasData ? currentPeriodValue - comparedPeriodValue : null;

    if (availabilityState === "hidden") {
        return null;
    }

    if (availabilityState === "locked") {
        return (
            <Card data-automation="conversion-rate-pop-widget">
                <ConversionRateCardHeader
                    title={translate(CONVERSION_RATE_TRANSLATION_KEYS.title)}
                    tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.tooltip)}
                    dateRangeText={dateRangeText}
                    countryText={countryText}
                    countryCode={countryCode}
                />
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                    padding="24px"
                    gap="8px"
                    flex="1"
                >
                    <Text fontVariant="subtitle2">{translate(CONVERSION_RATE_TRANSLATION_KEYS.lockedTitle)}</Text>
                    <IconButton
                        type="trial"
                        iconName="sparkles"
                        onClick={() => openHooksModalV5("conversionRate")}
                    >
                        {translate(CONVERSION_RATE_TRANSLATION_KEYS.lockedCta)}
                    </IconButton>
                </Flex>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <Card data-automation="conversion-rate-pop-widget">
                <Flex
                    flexDirection="column"
                    padding="24px"
                    gap="24px"
                    flex="1"
                >
                    <ConversionRatePeriodRowLoader />
                    <ConversionRatePeriodRowLoader />
                </Flex>
            </Card>
        );
    }

    return (
        <Card data-automation="conversion-rate-pop-widget">
            <ConversionRateCardHeader
                title={translate(CONVERSION_RATE_TRANSLATION_KEYS.title)}
                tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.tooltip)}
                dateRangeText={dateRangeText}
                countryText={countryText}
                countryCode={countryCode}
            />
            {isError || !hasData ? (
                <ConversionRateEmptyState emptyStateType={resolvedEmptyStateType} />
            ) : (
                <Flex
                    flexDirection="column"
                    padding="24px"
                    gap="24px"
                    flex="1"
                >
                    <ConversionRatePeriodRow
                        periodText={durationObject.forWidget[1]}
                        value={comparedPeriodValue}
                        barColor={PERIOD_COLORS.compared}
                    />
                    <ConversionRatePeriodRow
                        periodText={durationObject.forWidget[0]}
                        value={currentPeriodValue}
                        barColor={PERIOD_COLORS.current}
                        changeValue={changeValue}
                        changeLabel={compareModeLabel}
                    />
                </Flex>
            )}
        </Card>
    );
};
